/**
 * The editor's tool settings and their defaults. No imports, so build
 * scripts can read the defaults too (vite.config.ts writes the bottom bar's
 * default state into the static page, #1230). EditorContext re-exports both.
 */

export interface ToolSettings {
  activeStampId: string | null;
  brushSize: number;
  spacing: number;
  rotation: number;
  rotateAlong: boolean;
  offsetDistance: number;
  joinType: "miter" | "round" | "bevel";
  holeRadius: number;
  fillRotation: number;
  /** Master snap toggle — when false, every child snap behaviour
   *  below is disabled regardless of its own flag. The 4 children
   *  (angle / point / point-constraint / sizes) split the old
   *  `snapToAxis` toggle into independently controllable pieces
   *  (issue #98). Holding Shift during a drag still forces the
   *  angle + sizes paths on ad-hoc, orthogonal to these flags. */
  snapMaster: boolean;
  /** Snap drag angle to common multiples (0°/15°/30°/45°/60°/75°/90°)
   *  while a slot / polyline segment is being drawn. */
  snapAngle: boolean;
  /** Snap drawing / dragging vertices onto existing anchors, centres,
   *  and bbox-edge midpoints of other construction lines. */
  snapPoint: boolean;
  /** When a point snap fires on release, persist it as a coincident
   *  constraint so chain enforcement keeps the pair pinned. With
   *  this off, the geometric snap still happens visually but the
   *  relationship isn't recorded. */
  snapPointConstraint: boolean;
  /** Constrain rectangles / ellipses to squares / circles on drag —
   *  the half of the old `snapToAxis` toggle that's *not* about
   *  angles. */
  snapSizes: boolean;
  /** Nudge a dragged shape so the handle point closest to where the
   *  user first touched lands on the nearest cutting-mat grid
   *  intersection. No visual highlight — the snap is silent. */
  snapGrid: boolean;
  /** Snap a construction-line vertex being drawn onto the nearest point
   *  *along* any existing visible construction line — not just its anchors
   *  (issue #323). Takes priority over grid snap; anchor / point snap still
   *  wins over it. */
  snapLine: boolean;
  /** Draw rectangles / ellipses (and resize selections) symmetrically
   *  about the start point instead of corner-to-corner — the persistent
   *  equivalent of holding Alt / Option, so it works on keyboard-less
   *  devices (issue #389). Holding Alt during a drag forces it on
   *  ad-hoc, orthogonal to this flag. */
  snapCenter: boolean;
  /** Stitch tool — chisel profile. */
  stitchShape: "round" | "slant" | "diamond";
  /** Center-to-center distance between stitch holes (mm). */
  stitchPitch: number;
  /** Hole footprint dimension (mm) — diameter for round, long axis for
   *  slant / diamond. */
  stitchSize: number;
  /** Slant chisel angle (degrees) relative to the local stitch direction. */
  stitchSlantAngle: number;
  /** Round sub-tool — when true (default), a commit applies the
   *  chosen radius to every round-eligible corner of the targeted
   *  construction line in one go. When false, only the corner the
   *  user grabbed is rounded. Holding Ctrl/Cmd during a Round drag
   *  forces single-corner regardless of this setting (issue #100
   *  follow-up). */
  roundAllCorners: boolean;
  /** Show/hide on-canvas value rays (#745), remembered PER MODE. `null` means
   *  "use the mode default" — construction defaults ON, the leather / hardware /
   *  stamping modes default OFF; once the user toggles rays in a mode the
   *  explicit boolean sticks. */
  raysConstruction: boolean | null;
  /** Leather-mode rays, shared by the leather drawing tools, the Hole tool, and
   *  the Hardware tool (#728). */
  raysLeather: boolean | null;
  /** Size / rotation overlay for the Stamping tools (#920). */
  raysStamping: boolean | null;
  /** Regular-polygon drawing (#928), shared by every tool with a polygon kind
   *  (Construction / Shape / Cutout / Stitch). `polygonSides` is the vertex
   *  count for the regular drag-out — null means "not set" (the default, 6,
   *  applies; #931 will treat it as "derive from the drag");
   *  `polygonRadius` (mm, circumscribed) pins the size when set — null means
   *  "size from the drag"; `polygonEquilateral` forces the regular drag-out
   *  gesture without holding the size-snap modifier. */
  polygonSides: number | null;
  polygonRadius: number | null;
  /** Exact side length (mm) for the regular polygon (#934) — pins every side;
   *  mutually exclusive with `polygonRadius` (both define the size). */
  polygonSideLength: number | null;
  /** Polyline presets (#934 follow-up): a set Segment length pins every
   *  placed segment; a set Angle step snaps each segment's direction to
   *  multiples of it (like the Line kind's Angle step). null = free. */
  polylineSegmentLength: number | null;
  polylineAngleDeg: number | null;
  polygonEquilateral: boolean;
}

export function defaultSettings(): ToolSettings {
  return {
    activeStampId: null,
    brushSize: 4,
    spacing: 0,
    rotation: 0,
    rotateAlong: true,
    offsetDistance: -3,
    joinType: "round",
    holeRadius: 1.5,
    fillRotation: 0,
    snapMaster: true,
    snapAngle: false,
    snapPoint: true,
    snapPointConstraint: false,
    snapSizes: false,
    snapGrid: false,
    snapLine: false,
    snapCenter: false,
    stitchShape: "slant",
    stitchPitch: 3.38,
    // For the default slant chisel, `stitchSize` is interpreted as tooth
    // width — the perpendicular distance between the outer/inner ends of the
    // slot. 1.8 mm is the most commonly stocked diagonal-iron tooth width.
    stitchSize: 1.8,
    // Negative leans the slot the opposite direction from the y-down default
    // (≈ "/" against the seam) — matches the convention most pricking irons
    // are ground for.
    stitchSlantAngle: -45,
    // Round sub-tool default: apply to every eligible corner of the
    // line. Most rounded-leather shapes (corners on a wallet back,
    // a luggage tag, a key fob) want symmetrical corners, so "all"
    // is the common case. Ctrl/Cmd modifier overrides to single.
    roundAllCorners: true,
    // Rays default per mode (#745): null → use the mode default (construction
    // on, leather off). A user toggle replaces null with an explicit boolean.
    raysConstruction: null,
    raysLeather: null,
    raysStamping: null,
    // Regular-polygon drawing defaults (#928): sides unset (hexagon default),
    // size from the drag, regular mode only while the size-snap modifier /
    // toggle is active.
    polygonSides: null,
    polygonRadius: null,
    polygonSideLength: null,
    polylineSegmentLength: null,
    polylineAngleDeg: null,
    polygonEquilateral: false,
  };
}
